/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

const EE_CHECK_API_CACHE_STORAGE_KEY = 'ee-check.api-cache';
const EE_CHECK_API_CACHE_TTL_MS = 30 * 60 * 1000;
const EE_CHECK_API_SESSION_STORAGE_QUOTA_BYTES = 5 * 1024 * 1024;

interface EeCheckApiCacheEntry<T> {
    cachedAt: number;
    result: T;
}

type EeCheckApiCacheStore = Record<string, EeCheckApiCacheEntry<unknown>>;

const inFlightRequests = new Map<string, Promise<unknown>>();
let inMemoryCacheStore: EeCheckApiCacheStore = {};

export interface EeCheckApiCacheStats {
    cacheEntryCount: number;
    cacheSizeBytes: number;
    storageQuotaBytes: number | null;
    storageUsageBytes: number | null;
}

function getSessionStorage(): Storage | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

function stableSerialize(value: unknown): string {
    if (Array.isArray(value)) {
        return `[${value.map((entry) => stableSerialize(entry)).join(',')}]`;
    }

    if (value && typeof value === 'object') {
        const objectEntries = Object.entries(value as Record<string, unknown>)
            .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
            .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableSerialize(entryValue)}`);

        return `{${objectEntries.join(',')}}`;
    }

    return JSON.stringify(value);
}

function hashString(value: string): string {
    let hash = 5381;

    for (let index = 0; index < value.length; index += 1) {
        hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
    }

    return `h${(hash >>> 0).toString(16)}`;
}

function readCacheStore(): EeCheckApiCacheStore {
    const storage = getSessionStorage();

    if (storage === null) {
        return inMemoryCacheStore;
    }

    let rawStore: string | null;

    try {
        rawStore = storage.getItem(EE_CHECK_API_CACHE_STORAGE_KEY);
    } catch {
        return inMemoryCacheStore;
    }

    if (!rawStore) {
        return {};
    }

    try {
        return JSON.parse(rawStore) as EeCheckApiCacheStore;
    } catch {
        return {};
    }
}

function writeCacheStore(store: EeCheckApiCacheStore): void {
    const storage = getSessionStorage();

    if (storage === null) {
        inMemoryCacheStore = store;
        return;
    }

    const serializedStore = JSON.stringify(store);

    try {
        storage.setItem(EE_CHECK_API_CACHE_STORAGE_KEY, serializedStore);
    } catch (error) {
        if (!isStorageQuotaExceededError(error)) {
            inMemoryCacheStore = store;
            return;
        }

        const reducedStore = reduceCacheStoreToFitQuota(store);

        try {
            storage.setItem(
                EE_CHECK_API_CACHE_STORAGE_KEY,
                JSON.stringify(reducedStore),
            );
        } catch {
            inMemoryCacheStore = reducedStore;

            try {
                storage.removeItem(EE_CHECK_API_CACHE_STORAGE_KEY);
            } catch {
                // Storage access is unavailable. The cache remains in memory only.
            }
        }
    }
}

function getCacheStoreSizeBytes(store: EeCheckApiCacheStore): number {
    if (Object.keys(store).length === 0) {
        return 0;
    }

    return new Blob([JSON.stringify(store)]).size;
}

function getSessionStorageUsageBytes(): number | null {
    const storage = getSessionStorage();

    if (storage === null) {
        return null;
    }

    try {
        let totalBytes = 0;

        for (let index = 0; index < storage.length; index += 1) {
            const key = storage.key(index);

            if (key === null) {
                continue;
            }

            const value = storage.getItem(key) ?? '';
            totalBytes += new Blob([key]).size + new Blob([value]).size;
        }

        return totalBytes;
    } catch {
        return null;
    }
}

async function getStorageCapacityBytes(cacheSizeBytes: number): Promise<{
    quotaBytes: number;
    usageBytes: number;
}> {
    const sessionStorageUsageBytes = getSessionStorageUsageBytes();

    if (sessionStorageUsageBytes !== null) {
        return {
            quotaBytes: EE_CHECK_API_SESSION_STORAGE_QUOTA_BYTES,
            usageBytes: sessionStorageUsageBytes,
        };
    }

    try {
        const estimate = await navigator.storage?.estimate();
        const quotaBytes = estimate?.quota;
        const usageBytes = estimate?.usage;

        if (
            typeof quotaBytes === 'number' &&
            Number.isFinite(quotaBytes) &&
            typeof usageBytes === 'number' &&
            Number.isFinite(usageBytes)
        ) {
            return { quotaBytes, usageBytes };
        }
    } catch {
        // Fall back to the active in-memory cache when browser storage details are unavailable.
    }

    return {
        quotaBytes: EE_CHECK_API_SESSION_STORAGE_QUOTA_BYTES,
        usageBytes: cacheSizeBytes,
    };
}

function pruneCacheStore(store: EeCheckApiCacheStore, now: number = Date.now()): EeCheckApiCacheStore {
    return Object.fromEntries(
        Object.entries(store).filter(([, entry]) => now - entry.cachedAt <= EE_CHECK_API_CACHE_TTL_MS),
    );
}

function hasPrunedCacheEntries(
    store: EeCheckApiCacheStore,
    prunedStore: EeCheckApiCacheStore,
): boolean {
    return Object.keys(store).length !== Object.keys(prunedStore).length;
}

function isStorageQuotaExceededError(error: unknown): boolean {
    return error instanceof DOMException && (
        error.code === 22 ||
        error.code === 1014 ||
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    );
}

function reduceCacheStoreToFitQuota(store: EeCheckApiCacheStore): EeCheckApiCacheStore {
    const sortedEntries = Object.entries(store).sort(
        ([, leftEntry], [, rightEntry]) => leftEntry.cachedAt - rightEntry.cachedAt,
    );
    const reducedStore: EeCheckApiCacheStore = Object.fromEntries(sortedEntries);

    while (Object.keys(reducedStore).length > 0) {
        const serializedStore = JSON.stringify(reducedStore);

        if (new Blob([serializedStore]).size <= EE_CHECK_API_SESSION_STORAGE_QUOTA_BYTES) {
            return reducedStore;
        }

        const oldestCacheKey = Object.keys(reducedStore)[0];

        delete reducedStore[oldestCacheKey];
    }

    return {};
}

export function buildEeCheckApiCacheKey(url: string, payload: unknown): string {
    return hashString(stableSerialize({ payload, url }));
}

export function clearEeCheckApiCache(): void {
    inFlightRequests.clear();
    inMemoryCacheStore = {};

    const storage = getSessionStorage();

    if (storage === null) {
        return;
    }

    try {
        storage.removeItem(EE_CHECK_API_CACHE_STORAGE_KEY);
    } catch {
        // Storage access is unavailable. The cache remains in memory only.
    }
}

export function getOrCreateEeCheckApiInFlightRequest<T>(
    cacheKey: string,
    createRequest: () => Promise<T>,
): Promise<T> {
    const existingRequest = inFlightRequests.get(cacheKey);

    if (existingRequest) {
        return existingRequest as Promise<T>;
    }

    const request = Promise.resolve().then(createRequest);

    inFlightRequests.set(cacheKey, request);
    request.then(
        () => {
            if (inFlightRequests.get(cacheKey) === request) {
                inFlightRequests.delete(cacheKey);
            }
        },
        () => {
            if (inFlightRequests.get(cacheKey) === request) {
                inFlightRequests.delete(cacheKey);
            }
        },
    );

    return request;
}

export async function getEeCheckApiCacheStats(): Promise<EeCheckApiCacheStats> {
    const store = readCacheStore();
    const prunedStore = pruneCacheStore(store);
    const cacheEntryCount = Object.keys(prunedStore).length;
    const cacheSizeBytes = getCacheStoreSizeBytes(prunedStore);
    const storageCapacity = await getStorageCapacityBytes(cacheSizeBytes);

    if (hasPrunedCacheEntries(store, prunedStore)) {
        writeCacheStore(prunedStore);
    }

    return {
        cacheEntryCount,
        cacheSizeBytes,
        storageQuotaBytes: storageCapacity.quotaBytes,
        storageUsageBytes: storageCapacity.usageBytes,
    };
}

export function readEeCheckApiCache<T>(cacheKey: string): T | null {
    const now = Date.now();
    const store = readCacheStore();
    const prunedStore = pruneCacheStore(store, now);
    const cacheEntry = prunedStore[cacheKey];

    if (hasPrunedCacheEntries(store, prunedStore)) {
        writeCacheStore(prunedStore);
    }

    if (!cacheEntry) {
        return null;
    }

    return cacheEntry.result as T;
}

export function writeEeCheckApiCache<T>(cacheKey: string, result: T): void {
    const prunedStore = pruneCacheStore(readCacheStore());

    prunedStore[cacheKey] = {
        cachedAt: Date.now(),
        result,
    };

    writeCacheStore(prunedStore);
}
